import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ViewType } from '../data/homepageData';
import { CREATOR_FEATURES, BRAND_FEATURES } from '../data/homepageData';

interface FeaturesGridProps {
  view: ViewType;
}

/**
 * 6-card features grid that switches content based on the active view.
 * Cards animate in with staggered delays on scroll via framer-motion whileInView.
 */
const FeaturesGrid: React.FC<FeaturesGridProps> = ({ view }) => {
  const features = view === 'creator' ? CREATOR_FEATURES : BRAND_FEATURES;

  const sectionLabel = view === 'creator' ? 'CREATOR TOOLS' : 'BRAND TOOLS';
  const headlineAccent = view === 'creator' ? 'earn more' : 'scale UGC';
  const description =
    view === 'creator'
      ? 'From campaign discovery to payout tracking — your complete creator toolkit.'
      : 'From campaign launch to performance analytics — your complete brand command center.';

  return (
    <section
      id="features"
      className="relative"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 mb-4"
            style={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--cv-accent)',
            }}
          >
            <span
              style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }}
            />
            {sectionLabel}
            <span
              style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }}
            />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold tracking-tight mb-4"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.02em',
            }}
          >
            Everything you need to{' '}
            <span className="cv-text-gradient">{headlineAccent}</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto"
            style={{
              fontSize: 'var(--text-base)',
              color: 'var(--cv-text-muted)',
              maxWidth: '560px',
              lineHeight: 1.7,
            }}
          >
            {description}
          </motion.p>
        </div>

        {/* 6-card grid */}
        <div
          className="grid gap-5 mt-14"
          style={{
            gridTemplateColumns: 'repeat(3, 1fr)',
          }}
        >
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{
                duration: 0.7,
                delay: (i % 3) * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group p-8 transition-all duration-200 cursor-default"
              style={{
                background: 'var(--cv-surface)',
                border: '1px solid var(--cv-border)',
                borderRadius: 'var(--radius-lg)',
              }}
              whileHover={{
                y: -2,
                borderColor: 'var(--cv-border-accent)',
                boxShadow: '0 0 40px rgba(168, 85, 247, 0.06)',
              }}
            >
              {/* Feature icon */}
              <div
                className={cn(
                  'w-12 h-12 flex items-center justify-center mb-5 text-[22px]',
                  feature.iconClass,
                )}
                style={{ borderRadius: 'var(--radius-md)' }}
              >
                {feature.icon}
              </div>

              <h3
                className="font-bold mb-2 tracking-tight"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-lg)',
                  letterSpacing: '-0.01em',
                }}
              >
                {feature.title}
              </h3>

              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--cv-text-muted)',
                  lineHeight: 1.6,
                }}
              >
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Responsive grid fix — injected inline for simplicity */}
      <style>{`
        @media (max-width: 900px) {
          #features .grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          #features .grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default FeaturesGrid;
